import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../auth/AuthAllapot.tsx';
import { supabase } from '../lib/supabase.ts';
import { SZEREP_CIMKE, type TagSzerep } from '../lib/tipusok.ts';
import { Betolto, Gomb, HibaUzenet, Kartya, Parbeszed, Valaszto } from '../ui/elemek.tsx';

interface Tag { user_id: string; szerep: TagSzerep; profil: { nev: string } | null }

export default function Atosztas({ leadId, officeId, felelos }: { leadId: string; officeId: string; felelos: string | null }) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const [valasztott, setValasztott] = useState('');
  const [nyitva, setNyitva] = useState(false);
  const tagok = useQuery({
    queryKey: ['atosztas-tagok', officeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('office_members')
        .select('user_id, szerep, profil:profiles(nev)')
        .eq('office_id', officeId)
        .eq('aktiv', true);
      if (error) throw error;
      return data as unknown as Tag[];
    },
  });
  // Csak a kész tag (belépett, van TOTP-ja) kaphat leadet; a többit jelöljük (az adatbázis is megköveteli).
  const keszTagok = useQuery({
    queryKey: ['atosztas-kesz', officeId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('kollegak_kesz', { p_office_id: officeId });
      if (error) throw error;
      return new Map<string, boolean>(data.map((m) => [m.user_id, m.kesz]));
    },
  });
  const mentes = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('leads').update({ assigned_to: valasztott }).eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: async () => {
      setNyitva(false);
      setValasztott('');
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['lead', leadId] }),
        qc.invalidateQueries({ queryKey: ['esemenyek', leadId] }),
        qc.invalidateQueries({ queryKey: ['leadek'] }),
      ]);
    },
  });
  const nev = tagok.data?.find((t) => t.user_id === valasztott)?.profil?.nev ?? '';
  const szerepek: TagSzerep[] = ['vezeto', 'ertekesito'];
  return (
    <Kartya>
      <h2 className="mb-3 text-[17px] font-medium tracking-[-0.015em]">Átosztás</h2>
      {tagok.isPending && <Betolto />}
      <HibaUzenet hiba={tagok.error ?? keszTagok.error} />
      {tagok.data && (
        <div className="grid gap-3">
          <Valaszto label="Új felelős" value={valasztott} onChange={(e) => setValasztott(e.target.value)}>
            <option value="">Válassz kollégát…</option>
            {szerepek.map((sz) => (
              <optgroup key={sz} label={SZEREP_CIMKE[sz]}>
                {tagok.data.filter((t) => t.szerep === sz).map((t) => {
                  const nemKesz = keszTagok.data?.get(t.user_id) === false;
                  return (
                    <option key={t.user_id} value={t.user_id} disabled={t.user_id === felelos || nemKesz}>
                      {t.profil?.nev ?? 'Névtelen'}{t.user_id === felelos ? ' (jelenlegi)' : ''}{nemKesz ? ' (még nem lépett be)' : ''}
                    </option>
                  );
                })}
              </optgroup>
            ))}
          </Valaszto>
          <Gomb valtozat="masodlagos" disabled={valasztott === ''} onClick={() => setNyitva(true)}>Átadás</Gomb>
        </div>
      )}
      <Parbeszed nyitva={nyitva} cim="Lead átadása" onBezar={() => setNyitva(false)}>
        <p>Átadod {nev}-nek? {valasztott === session?.user.id ? 'Magadnak adod át; erről nem megy levél.' : 'E-mailben értesítjük.'}</p>
        <HibaUzenet hiba={mentes.error} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Gomb valtozat="masodlagos" onClick={() => setNyitva(false)}>Mégse</Gomb>
          <Gomb toltes={mentes.isPending} onClick={() => mentes.mutate()}>Átadom</Gomb>
        </div>
      </Parbeszed>
    </Kartya>
  );
}
